"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { createScrollLock, type ScrollLock } from "@/lib/host-panel/scroll-lock";
import { LENIS_ENGINE_OPTIONS } from "@/lib/motion/lenis-engine";
import { TB_SCROLL_TO, easeOutCubic } from "@/lib/motion/motion-tokens";

/**
 * Registry of the panel's stacked layers (handoff §4, §16).
 *
 * Layers built on `@tb/dialog` and `@tb/drawer` already handle focus trapping, `inert`
 * and the `body` overflow. Keep this registry to what Radix cannot know: the Lenis
 * smooth scroll, the root element's `overflow`, and the ordering between layers that
 * are not Radix at all (facet popover, own bottom sheet).
 */

type LayerEntry = {
  close: () => void;
};

type LenisLike = {
  stop: () => void;
  start: () => void;
  destroy: () => void;
  raf: (time: number) => void;
  scrollTo: (
    target: number | HTMLElement,
    options?: { immediate?: boolean; force?: boolean; offset?: number; duration?: number; easing?: (t: number) => number },
  ) => void;
};

/**
 * Mutable area state, in a stable object rather than refs.
 *
 * Nothing here takes part in rendering — it is coordination, so it can be read and
 * written without the `ref.current` access rules.
 */
type PanelLayersStore = {
  stack: LayerEntry[];
  lenis: LenisLike | null;
  scrollLock: ScrollLock;
};

type PanelLayersContextValue = {
  /** Registers the open layer and returns its release. */
  registerLayer: (entry: LayerEntry) => () => void;
  /** The area is ready to scroll: smooth scroll is up, failed, or timed out. */
  isReady: boolean;
  /**
   * Jumps the area to the top, with no travel.
   *
   * Lives here because whoever installs the smooth scroll is the only one who can
   * reposition it: Lenis keeps its own position and reapplies it every frame, so
   * writing to `window.scrollTo` only desyncs it and the next frame snaps back.
   */
  scrollToTop: () => void;
  /**
   * Glides the area to an element, landing it clear of the sticky header — the product's one
   * smooth scroll (`TB_SCROLL_TO`). A jump under reduced motion.
   */
  scrollToElement: (element: HTMLElement) => void;
};

const PanelLayersContext = createContext<PanelLayersContextValue | null>(null);

/**
 * Says, on the root element, that **this** lock is the one holding the scroll.
 *
 * It exists for CSS to read. Every layer here is a Radix dialog or drawer, and Radix ships
 * `react-remove-scroll`, which locks by injecting `body[data-scroll-locked] { overflow: hidden
 * !important }`. Two locks for one scroll, and the second one breaks the header: the document
 * scrolls on the root, so turning `body` into a scroll container leaves the sticky header
 * resolving against a box that is not scrolled, and it renders at its static position — off the
 * top of the screen by exactly the scroll offset. Measured in Chromium: at `scrollY` 950 the
 * header's `top` goes from 0 to -950. Reported 2026-10-14, as the header vanishing while the
 * quantity dialog is open.
 *
 * `globals.css` uses this attribute to stand Radix's lock down, and **only while ours is up**.
 * That condition is the point: the attribute catalogue's one layer that does not register here
 * is the catalog's attribute sheet (`facet-rail.tsx`), which relies on Radix's lock alone and
 * must keep it.
 */
export const PANEL_SCROLL_LOCK_ATTRIBUTE = "data-panel-scroll-locked";

function createStore(): PanelLayersStore {
  const store: Partial<PanelLayersStore> = { stack: [], lenis: null };

  store.scrollLock = createScrollLock({
    lock: () => {
      store.lenis?.stop();
      // Hiding the overflow does not shift content because the root reserves the
      // scrollbar gutter (`globals.css`, `@layer base`). `scroll-lock.spec.ts` proves
      // the dependency instead of trusting this line.
      document.documentElement.style.overflow = "hidden";
      document.documentElement.setAttribute(PANEL_SCROLL_LOCK_ATTRIBUTE, "");
    },
    unlock: () => {
      document.documentElement.style.overflow = "";
      document.documentElement.removeAttribute(PANEL_SCROLL_LOCK_ATTRIBUTE);
      store.lenis?.start();
    },
  });

  return store as PanelLayersStore;
}

/** Ceiling for waiting on smooth scroll before revealing the area (handoff §5). */
const PANEL_READY_TIMEOUT_MS = 2000;

export function PanelLayersProvider({ children }: { children: React.ReactNode }) {
  const storeRef = useRef<PanelLayersStore | null>(null);
  const [isReady, setIsReady] = useState(false);

  /**
   * Lazy creation, only from a callback or effect and never during render — which is
   * what lets the store be read and written outside the hook rules.
   */
  const getStore = useCallback(() => {
    storeRef.current ??= createStore();

    return storeRef.current;
  }, []);

  const registerLayer = useCallback(
    (entry: LayerEntry) => {
      const store = getStore();

      store.stack = [...store.stack, entry];
      const release = store.scrollLock.acquire();

      return () => {
        store.stack = store.stack.filter((candidate) => candidate !== entry);
        release();
      };
    },
    [getStore],
  );

  // One listener for the whole area: Esc closes the top layer only. One listener per
  // layer would close them all at once.
  useEffect(() => {
    const store = getStore();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      const top = store.stack.at(-1);
      if (top === undefined) return;

      top.close();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [getStore]);

  /*
   * Smooth scroll and the area's entry gate, in a single effect.
   *
   * The gate exists because `import("lenis")` is async, which otherwise leaves a window where the
   * page scrolls natively and switches physics mid-gesture. Its lock goes through the **same
   * counter** as the stacked layers, so a drawer opening in that window adds a lock instead of
   * releasing what the gate held.
   */
  useEffect(() => {
    const store = getStore();
    let mounted = true;
    let rafId: number | null = null;
    let released = false;

    const release = store.scrollLock.acquire();

    const openGate = () => {
      if (released) return;

      released = true;
      release();
      setIsReady(true);
    };

    const safety = setTimeout(openGate, PANEL_READY_TIMEOUT_MS);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // No smooth scroll to load means no hand-off to hide.
      clearTimeout(safety);
      openGate();

      return () => {
        if (!released) {
          released = true;
          release();
        }
      };
    }

    void import("lenis")
      .then(({ default: Lenis }) => {
        if (!mounted) return;

        /*
         * The panel keeps its own instance, and the numbers come from the one
         * place that defines them (`lib/motion/lenis-engine.ts`) — they were
         * four literals here.
         *
         * Not on the shared reference count, deliberately, and this is the one
         * surface left outside it: the instance is born stopped and started by
         * the panel's own gate, and `store.lenis` is read across the shell. It
         * already does the half that mattered — it destroys what it created — so
         * entering the panel from a public page finds no engine to collide with,
         * because those pages now release theirs on unmount.
         */
        const instance = new Lenis(LENIS_ENGINE_OPTIONS);
        store.lenis = instance;

        // The gate's lock is still held here, so Lenis is born stopped: `start()`
        // comes from the counter's `unlock` when the gate opens.
        instance.stop();

        const tick = (time: number) => {
          instance.raf(time);
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        clearTimeout(safety);
        openGate();
      })
      .catch(() => {
        // Degrades to native scroll; opening the gate here spares the full ceiling.
        clearTimeout(safety);
        openGate();
      });

    return () => {
      mounted = false;
      clearTimeout(safety);
      if (rafId !== null) cancelAnimationFrame(rafId);
      store.lenis?.destroy();
      store.lenis = null;
      if (!released) {
        released = true;
        release();
      }
    };
  }, [getStore]);

  // Leaving the area with a layer open must not leave the document locked.
  useEffect(() => {
    const store = getStore();

    return () => store.scrollLock.releaseAll();
  }, [getStore]);

  const scrollToTop = useCallback(() => {
    const lenis = getStore().lenis;

    if (lenis === null) {
      // Reduced motion, or a failed import: the document owns the position.
      window.scrollTo({ top: 0, behavior: "instant" });

      return;
    }

    // `force` matters: with a layer open Lenis is stopped, and without it the request
    // would be dropped and the area would open mid-page once the layer closed.
    lenis.scrollTo(0, { immediate: true, force: true });
  }, [getStore]);

  const scrollToElement = useCallback(
    (element: HTMLElement) => {
      const lenis = getStore().lenis;
      // Measured here, against the document's own scroll, rather than handing Lenis the element:
      // Lenis adds the rect to its animated position, which trails the document while the view
      // that just changed is still being laid out, and the glide then lands off target.
      const top = Math.max(0, window.scrollY + element.getBoundingClientRect().top - TB_SCROLL_TO.offsetPx);

      if (lenis !== null) {
        lenis.scrollTo(top, { duration: TB_SCROLL_TO.durationS, easing: easeOutCubic });

        return;
      }

      // Reduced motion (Lenis is never loaded then) jumps; a failed import glides natively.
      const isReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top, behavior: isReduced ? "instant" : "smooth" });
    },
    [getStore],
  );

  const value = useMemo(
    () => ({ registerLayer, isReady, scrollToTop, scrollToElement }),
    [registerLayer, isReady, scrollToTop, scrollToElement],
  );

  return <PanelLayersContext.Provider value={value}>{children}</PanelLayersContext.Provider>;
}

/** Whether the area is ready to scroll. Same owner as the layers, by design. */
export function usePanelReady(): boolean {
  const context = useContext(PanelLayersContext);

  if (context === null) {
    throw new Error("usePanelReady must be used inside a PanelLayersProvider.");
  }

  return context.isReady;
}

const NO_SCROLL_RESET = () => {};

/**
 * Jumps the area to the top, aware of the smooth scroll.
 *
 * Degrades to a no-op outside the provider, matching its only caller: a link that
 * navigates without animating or repositioning is still a correct link, and the App
 * Router scrolls to the top on its own when nobody intercepts.
 */
export function usePanelScrollToTop(): () => void {
  const context = useContext(PanelLayersContext);

  return context?.scrollToTop ?? NO_SCROLL_RESET;
}

const NO_SCROLL_TO_ELEMENT: (element: HTMLElement) => void = () => {};

/**
 * Glides the area to an element, aware of the smooth scroll. A no-op outside the provider, like
 * {@link usePanelScrollToTop}: nothing moves rather than desyncing a scroll engine it cannot see.
 */
export function usePanelScrollToElement(): (element: HTMLElement) => void {
  const context = useContext(PanelLayersContext);

  return context?.scrollToElement ?? NO_SCROLL_TO_ELEMENT;
}

/**
 * Declares a layer open: locks scrolling while it lasts and joins the Esc queue.
 * `onClose` may be recreated on every render at no cost.
 */
export function usePanelLayer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }): void {
  const context = useContext(PanelLayersContext);

  if (context === null) {
    throw new Error("usePanelLayer must be used inside a PanelLayersProvider.");
  }

  const { registerLayer } = context;
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    return registerLayer({ close: () => onCloseRef.current() });
  }, [isOpen, registerLayer]);
}
